import React, { FC, useMemo } from 'react'
import { Box, Typography, Paper, Alert } from '@mui/material'
import Grid from '@mui/material/Grid'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import {
  MetricsBarChart,
  formatDurationToMinutes,
} from '@/apps/legacy/components/portal/MetricsChart'
import {
  useMetricsSummaryQuery,
  useMetricsAnalyticsQuery,
  usePharmacyListQuery,
} from '@/features/internal/metrics'
import { Dayjs } from 'dayjs'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

interface KPICardProps {
  title: string
  value: string | number
  unit?: string
}

const KPICard: FC<KPICardProps> = ({ title, value, unit = '' }) => {
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        backgroundColor: colorTokens.background.paper,
        border: `1px solid ${colorTokens.border.light}`,
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: colorTokens.text.secondary, mb: 1 }}
      >
        {title}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          color: colorTokens.text.primary,
        }}
      >
        {value}
        {unit && (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              color: colorTokens.text.secondary,
            }}
          >
            {unit}
          </Typography>
        )}
      </Typography>
    </Paper>
  )
}

interface OverviewTabProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  selectedPharmacyId: string
}

export const OverviewTab: FC<OverviewTabProps> = ({
  startDate,
  endDate,
  selectedPharmacyId,
}) => {
  const { pharmacyData } = usePharmacyListQuery()

  // リクエストパラメータを構築
  const requestParams = useMemo(() => {
    if (!startDate || !endDate || !pharmacyData) return null

    const dateRange = {
      start: startDate.format('YYYY-MM-DD'),
      end: endDate.format('YYYY-MM-DD'),
    }

    // 未選択は「絞らない」= キーを送らない。本部なら全社、それ以外は自店になる
    const targetParams =
      selectedPharmacyId && selectedPharmacyId !== ''
        ? { pharmacy_ids: [selectedPharmacyId] }
        : {}

    return {
      dateRange,
      targetParams,
    }
  }, [startDate, endDate, selectedPharmacyId, pharmacyData])

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useMetricsSummaryQuery({
    params: {
      date_range: requestParams?.dateRange ?? { start: '', end: '' },
      include_previous_period: true,
      ...requestParams?.targetParams,
    },
    enabled: !!requestParams,
  })

  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useMetricsAnalyticsQuery({
    params: {
      date_range: requestParams?.dateRange ?? { start: '', end: '' },
      ...requestParams?.targetParams,
    },
    enabled: !!requestParams,
  })

  const loading = summaryLoading || analyticsLoading
  const errors = [summaryError?.message, analyticsError?.message].filter(
    Boolean
  )
  const error = errors.length > 0 ? errors.join('、') : null

  const formatExchangeDistribution = () => {
    if (!analytics?.distributions.exchange_count) return []
    return Object.entries(analytics.distributions.exchange_count).map(
      ([range, count]) => ({
        やりとり回数: range + '回',
        件数: count,
      })
    )
  }

  const formatMedicationData = () => {
    if (!analytics?.distributions.medication_count_pattern) return []
    return Object.entries(analytics.distributions.medication_count_pattern).map(
      ([range, pattern]) => ({
        薬剤数範囲: range + '剤',
        平均指導時間: pattern.avg_duration,
      })
    )
  }

  const formatHourlyPattern = () => {
    if (!analytics?.distributions.hourly_pattern) return []

    // 時間帯ラベルの定義
    const timeLabels: Record<string, string> = {
      '00-09': '0-9時',
      '09-12': '9-12時',
      '12-15': '12-15時',
      '15-18': '15-18時',
      '18-24': '18-24時',
    }

    // 時間順でソート（0時から24時の順）
    const timeOrder = ['00-09', '09-12', '12-15', '15-18', '18-24']

    return timeOrder.map((timeRange) => {
      const data = analytics.distributions.hourly_pattern[timeRange] || {
        count: 0,
        avg_duration: 0,
      }
      return {
        時間帯: timeLabels[timeRange] || timeRange,
        平均指導時間: data.avg_duration,
        件数: data.count,
      }
    })
  }

  return (
    <Box>
      {loading && <LoadingIndicator />}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 基本指標 - KPIカード */}
      {summary && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard title="来局数" value={summary.total_count} unit="件" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="SOAP生成数"
                value={`${summary.soap_count || 0}件 (${
                  summary.soap_rate?.toFixed(1) || 0
                }%)`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="コピー数"
                value={`${summary.copy_count || 0}件 (${
                  summary.copy_rate?.toFixed(1) || 0
                }%)`}
              />
            </Grid>
          </Grid>

          {/* 2行目: その他の指標 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="平均指導時間"
                value={summary.average_duration}
                unit="秒"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="1剤あたり時間"
                value={summary.time_per_medication?.toFixed(1) || '-'}
                unit="秒/剤"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="平均やりとり回数"
                value={summary.average_exchange_count.toFixed(1)}
                unit="回"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <KPICard
                title="話速"
                value={
                  summary.speech_rate !== null
                    ? summary.speech_rate.toFixed(0)
                    : '-'
                }
                unit={summary.speech_rate !== null ? '文字/分' : ''}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* 業務パターン分析 */}
      {analytics && (
        <>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: colorTokens.text.primary,
            }}
          >
            業務パターン分析
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  時間帯別の平均指導時間
                </Typography>
                <MetricsBarChart
                  data={formatHourlyPattern()}
                  dataKey="平均指導時間"
                  xAxisKey="時間帯"
                  height={250}
                  color={colorTokens.blue[600]}
                  yAxisTickFormatter={formatDurationToMinutes}
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  薬剤数別の平均指導時間
                </Typography>
                <MetricsBarChart
                  data={formatMedicationData()}
                  dataKey="平均指導時間"
                  xAxisKey="薬剤数範囲"
                  height={250}
                  color={colorTokens.blue[600]}
                  yAxisTickFormatter={formatDurationToMinutes}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* 分布分析 */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: colorTokens.text.primary,
            }}
          >
            分布分析
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colorTokens.text.primary,
                    mb: 2,
                  }}
                >
                  患者とのやりとり回数の分布
                </Typography>
                <MetricsBarChart
                  data={formatExchangeDistribution()}
                  dataKey="件数"
                  xAxisKey="やりとり回数"
                  height={250}
                  color={colorTokens.status.success}
                />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
